/**
 * Cart Slide Drawer - Right Side Sliding Cart
 * Custom component for SICOTAS
 * 
 * Listens for 'cart:open', 'sideCart:open', and 'ajaxProduct:added' events
 * to open the right-side sliding cart drawer.
 */

(function() {
  'use strict';

  const CartSlideDrawer = {
    drawer: null,
    overlay: null,
    closeBtn: null,
    content: null,
    isOpen: false,
    ready: false,
    suppressAutoOpen: false,
    refreshSequence: 0,
    pendingMobileAddLoads: 0,
    variantComparePrices: new Map(),
    productPriceRequests: new Map(),
    quantityUpdateQueue: new Map(),
    bundleQuantityUpdateQueue: new Map(),

    init() {
      this.drawer = document.getElementById('cart-slide-drawer');
      this.overlay = document.getElementById('cart-slide-overlay');
      this.closeBtn = document.getElementById('cart-slide-drawer-close');
      this.content = document.getElementById('cart-slide-drawer-content');
      this.itemsWrap = document.getElementById('cart-slide-drawer-items-wrap');
      this.rememberComparePrices(this.itemsWrap);

      if (!this.drawer) {
        console.warn('Cart Slide Drawer: Drawer element not found');
        return;
      }

      this.bindEvents();
      this.bindCheckoutRestore();
      this.initRecommendationsScrollbar();
      this.initRecommendationSwatches();
      // 页面加载后尽早静默检测并迁移拆分商品导致的失效 variant，抢在用户做任何其他购物车操作
      // （比如加购别的商品）之前完成——那类操作本身会触发 Shopify 侧的购物车校验，把失效行直接
      // 删掉而不会补上新商品，所以越早检测越能避免"旧商品消失但没有新商品替换"的情况。
      this.migrateStaleCartVariants();
      this.ready = true;
      console.log('Cart Slide Drawer: Initialized');
    },

    resetDrawerCheckoutState() {
      document.querySelectorAll('.cart-slide-drawer__checkout-btn').forEach((button) => {
        button.classList.remove('is-loading', 'btn--loading', 'loading', 'disabled', 'is-disabled');
        button.disabled = false;
        button.removeAttribute('disabled');
        button.removeAttribute('aria-disabled');
        button.removeAttribute('aria-busy');
        button.removeAttribute('data-loading');
        button.style.removeProperty('pointer-events');
        button.style.removeProperty('opacity');

        // Mobile browsers may cache the loading markup itself. Always rebuild the
        // drawer label from the current total instead of relying on a specific loader.
        const total = document.getElementById('cart-slide-drawer-total')?.textContent?.trim() || '';
        button.innerHTML = 'Checkout &bull;&nbsp;<span id="cart-slide-drawer-checkout-total"></span>';
        const checkoutTotal = button.querySelector('#cart-slide-drawer-checkout-total');
        if (checkoutTotal) checkoutTotal.textContent = total;
      });
    },

    scheduleCheckoutRestore() {
      this.resetDrawerCheckoutState();
      requestAnimationFrame(() => this.resetDrawerCheckoutState());
      window.setTimeout(() => this.resetDrawerCheckoutState(), 100);
      window.setTimeout(() => this.resetDrawerCheckoutState(), 500);
    },

    bindCheckoutRestore() {
      window.addEventListener('pagehide', () => this.resetDrawerCheckoutState());
      window.addEventListener('pageshow', () => this.scheduleCheckoutRestore());
      window.addEventListener('focus', () => this.scheduleCheckoutRestore());
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          this.scheduleCheckoutRestore();
        }
      });
    },

    bindEvents() {
      // Open events
      document.addEventListener('cart:open', () => this.open());
      document.addEventListener('sideCart:open', () => this.open());
      
      // Listen for product added to cart - EComposer triggers this
      document.addEventListener('ajaxProduct:added', (e) => {
        // 重置抑制标记：本次加购流程默认应该在数据加载完成后打开抽屉
        this.suppressAutoOpen = false;
        const mobileAddLoading = this.beginMobileAddLoading();
        const addedProduct = e.detail?.product;
        const sectionHtml = addedProduct?.sections?.['cart-slide-drawer-content'] || null;
        // block-buy-buttons 在点击瞬间冻结 coupon ID。Klip 可能在 add.js
        // 返回前重绘组件，此时再查 DOM 已经拿不到 .active。
        // 只接受发起加购的商品组件明确传入的 coupon ID。不能从整个 document 猜测，
        // 否则会把商品卡片、其它商品或残留 Klip 组件上的 active coupon 错配给本商品。
        const activeCouponId = typeof e.detail?.klipCouponId === 'string'
          ? e.detail.klipCouponId
          : '';
        const previousKlipEntries = Array.isArray(e.detail?.previousKlipEntries)
          ? e.detail.previousKlipEntries
          : [];
        const addedItems = Array.isArray(addedProduct?.items) ? addedProduct.items : [addedProduct];
        const addedItemKeys = new Set(
          addedItems.filter(Boolean).map(item => String(item.key || '')).filter(Boolean)
        );
        const addedVariantIds = new Set(
          addedItems
            .filter(Boolean)
            .map(item => String(item.variant_id || item.id || ''))
            .filter(Boolean)
        );
        const cartUpdatePromise = this.persistKlipCouponForAddedItems(addedProduct, activeCouponId, previousKlipEntries, e.detail?.klipProductData)
          .catch(error => {
            console.warn('Cart Slide Drawer: Unable to persist Klip coupon', error);
            return null;
          })
          .then(persistedCart => this.refreshCart(
            persistedCart,
            persistedCart?.sections?.['cart-slide-drawer-content'] || sectionHtml
          ))
          .then((cart) => {
            // 如果 refreshCart 请求还在进行时用户已经手动关闭了抽屉，则不再强行重新打开
            if (!this.suppressAutoOpen && !this.isOpen) {
              this.open();
            }
            // Choose Your Perfect Match 走的是独立商品表单；加购完成后 Klip 可能尚未把
            // 优惠信息写回购物车。只对本次新增行使用页面已选中的优惠券即时补齐标签。
            if (cart && activeCouponId) {
              this.renderKlipDiscountsFromPageData(cart, {
                preferredCouponId: activeCouponId,
                itemKeys: addedItemKeys,
                variantIds: addedVariantIds
              });
              this.updateKlipAdjustedSummary(cart, {
                preferredCouponId: activeCouponId,
                itemKeys: addedItemKeys,
                variantIds: addedVariantIds
              });
            }
          })
          .catch(error => {
            console.warn('Cart Slide Drawer: Unable to complete add-to-cart refresh', error);
          })
          .finally(() => {
            if (mobileAddLoading) this.endMobileAddLoading();
          });
        if (e.detail) e.detail.cartUpdatePromise = cartUpdatePromise;
      });

      // Close events
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.close());
      }


      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.close());
      }

      const checkoutBtn = this.drawer.querySelector('.cart-slide-drawer__checkout-btn');
      if (checkoutBtn) {
        // Keep the inline redirect as a no-JavaScript fallback, but let this
        // handler own the normal flow so the loading state is applied first.
        checkoutBtn.onclick = null;
        checkoutBtn.addEventListener('click', () => {
          if (checkoutBtn.classList.contains('is-loading') || this.pendingMobileAddLoads > 0) return;

          const discountCodes = (checkoutBtn.dataset.discountCodes || '')
            .split(',')
            .map(code => code.trim())
            .filter(Boolean);
          const checkoutUrl = discountCodes.length
            ? '/checkout?discount=' + discountCodes.map(encodeURIComponent).join(',')
            : '/checkout';
          checkoutBtn.classList.add('is-loading');
          checkoutBtn.disabled = true;
          checkoutBtn.setAttribute('aria-busy', 'true');
          checkoutBtn.innerHTML = '<span class="cart-slide-drawer__checkout-text">Loading...</span>';
          window.location.href = checkoutUrl;
        });
      }

      // ESC key to close
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
        }
      });

      // Quantity buttons
      this.drawer.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-action]');
        if (!btn) return;

        const action = btn.dataset.action;
        const line = parseInt(btn.dataset.line, 10);
        const bundleId = btn.dataset.bundleId;

        if (action === 'increase') {
          // 支持普通商品和主商品Bundle
          let itemEl = this.drawer.querySelector(`.cart-slide-drawer__item[data-line="${line}"]`);
          if (!itemEl) {
            itemEl = this.drawer.querySelector(`.cart-slide-drawer__bundle[data-line="${line}"]`);
          }
          
          const qtyInput = itemEl?.querySelector('.cart-slide-drawer__quantity-input');
          const currentQty = qtyInput ? parseInt(qtyInput.value, 10) : 0;
          
          // 检查30件限制
          if (currentQty >= 30) {
            this.showStockWarning(itemEl, 'Max 30 per order');
            return;
          }
          
          // 检查库存上限
          const maxQty = parseInt(btn.dataset.maxQty, 10);
          if (maxQty && !isNaN(maxQty) && currentQty >= maxQty) {
            this.showStockWarning(itemEl, `Max ${maxQty} in stock`);
            return;
          }
          
          this.updateQuantity(line, 1);
        } else if (action === 'decrease') {
          this.updateQuantity(line, -1);
        } else if (action === 'remove') {
          this.removeItem(line);
        } else if (action === 'increase-bundle' && bundleId) {
          // 检查30件限制
          const bundleEl = this.drawer.querySelector(`.cart-slide-drawer__bundle[data-bundle-id="${bundleId}"]`);
          const bundleQtyInput = bundleEl?.querySelector('.cart-slide-drawer__bundle-quantity-input');
          const bundleCurrentQty = bundleQtyInput ? parseInt(bundleQtyInput.value, 10) : 0;
          
          if (bundleCurrentQty >= 30) {
            this.showBundleStockWarning(bundleEl, 'Max 30 per order');
            return;
          }
          
          this.updateBundleQuantity(bundleId, 1);
        } else if (action === 'decrease-bundle' && bundleId) {
          // 如果按钮被禁用（数量为1），不执行操作
          if (btn.classList.contains('is-disabled') || btn.disabled) {
            return;
          }
          this.updateBundleQuantity(bundleId, -1);
        } else if (action === 'remove-bundle' && bundleId) {
          this.removeBundleItems(bundleId);
        }
      });

      // Quantity input change
      this.drawer.addEventListener('change', (e) => {
        if (e.target.classList.contains('cart-slide-drawer__quantity-input')) {
          const line = parseInt(e.target.dataset.line, 10);
          let quantity = parseInt(e.target.value, 10);
          const itemEl = this.drawer.querySelector(`.cart-slide-drawer__item[data-line="${line}"]`);
          
          // 检查30件限制
          if (quantity > 30) {
            quantity = 30;
            e.target.value = 30;
            this.showStockWarning(itemEl, 'Max 30 per order');
          }
          
          if (quantity >= 1) {
            this.setQuantity(line, quantity);
          }
        } else if (e.target.classList.contains('cart-slide-drawer__bundle-quantity-input')) {
          const bundleId = e.target.dataset.bundleId;
          let quantity = parseInt(e.target.value, 10);
          const bundleEl = this.drawer.querySelector(`.cart-slide-drawer__bundle[data-bundle-id="${bundleId}"]`);
          
          // 检查30件限制
          if (quantity > 30) {
            quantity = 30;
            e.target.value = 30;
            this.showBundleStockWarning(bundleEl, 'Max 30 per order');
          }
          
          if (bundleId && quantity >= 1) {
            this.setBundleQuantity(bundleId, quantity);
          }
        }
      });
      
      // Discount code apply button
      this.drawer.addEventListener('click', (e) => {
        if (e.target.classList.contains('cart-slide-drawer__discount-btn')) {
          this.applyDiscountCode();
        }
      });
      
      // Discount code input - Enter key to apply
      this.drawer.addEventListener('keydown', (e) => {
        if (e.target.classList.contains('cart-slide-drawer__discount-input') && e.key === 'Enter') {
          e.preventDefault();
          this.applyDiscountCode();
        }
      });
      
      // Clear error when user starts typing
      this.drawer.addEventListener('input', (e) => {
        if (e.target.classList.contains('cart-slide-drawer__discount-input')) {
          this.hideDiscountError();
        }
      });
    },

    open() {
      if (!this.drawer) return;

      this.scheduleCheckoutRestore();
      this.isOpen = true;
      this.drawer.classList.add('is-active');
      this.drawer.setAttribute('aria-hidden', 'false');
      
      if (this.overlay) {
        this.overlay.classList.add('is-active');
      }

      document.body.classList.add('cart-slide-drawer-open');

      // Trap focus
      this.trapFocus();

      // 每次打开抽屉都静默检测并迁移拆分商品导致的失效 variant，不阻塞抽屉打开动画
      this.migrateStaleCartVariants();

      console.log('Cart Slide Drawer: Opened');
    },

    close() {
      if (!this.drawer) return;

      this.isOpen = false;
      // 用户在加购的 refreshCart 请求完成前主动关闭了抽屉，请求完成后不应再自动重新打开
      this.suppressAutoOpen = true;
      this.drawer.classList.remove('is-active');
      this.drawer.setAttribute('aria-hidden', 'true');
      
      if (this.overlay) {
        this.overlay.classList.remove('is-active');
      }

      document.body.classList.remove('cart-slide-drawer-open');

      // Dispatch close event
      document.dispatchEvent(new CustomEvent('cart:close'));

      // If on cart page, reload to refresh the cart content
      if (window.location.pathname === '/cart' || window.location.pathname.includes('/cart')) {
        window.location.reload();
      }

      console.log('Cart Slide Drawer: Closed');
    },

    trapFocus() {
      // Simple focus trap - focus the close button when opened
      if (this.closeBtn) {
        setTimeout(() => this.closeBtn.focus(), 100);
      }
    },

    async refreshCart(prefetchedCart, sectionHtml) {
      const sequence = ++this.refreshSequence;
      try {
        this.showLoading();

        // 加购后 fetch-interceptor.js 已经在读取写后快照；复用它，避免抽屉再并发打一条。
        // 迁移完成时 migrateStaleCartVariants 会把它内部已经现读过的干净购物车数据
        // 直接传进来（prefetchedCart），不用再多发一次可能失败的请求。
        let cart = prefetchedCart || await this.getCartSnapshot();
        if (sequence !== this.refreshSequence) return null;
        if (!cart) {
          // 迁移/加购这类场景购物车接口偶尔会因为网络抖动/429（这个店铺这段时间反复
          // 出现）短暂失败，之前只重试一次、固定等 1.2 秒不够用，命中较长的限流窗口
          // 时依然会失败，导致抽屉内容和顶部购物车数量一直停在旧状态、要手动刷新页面
          // 才能看到正确结果——实测就是"加购/迁移后顶部购物车数量不显示，刷新又恢复
          // 正常"这个问题的根因。改成跟 variant-migration.js 的 fetchWithRetry 一样的
          // 指数退避多试几次
          cart = await this.getCartSnapshotWithRetry();
        }
        if (sequence !== this.refreshSequence) return null;
        if (!cart) throw new Error('Unable to load cart');

        // 顶部数量先用 cart 快照更新；有 bundled section 时 renderCart 会直接复用，
        // 旧调用方没有携带 section 时才回退到独立的 Section Rendering 请求。
        this.updateHeaderCartCount(this.calculateDisplayCount(cart));

        await this.renderCart(cart, sectionHtml, sequence);
        if (sequence !== this.refreshSequence) return null;

        console.log('Cart Slide Drawer: Cart refreshed', cart);
        return cart;
      } catch (error) {
        if (sequence !== this.refreshSequence) return null;
        console.error('Cart Slide Drawer: Error refreshing cart', error);
      }
    },

    beginMobileAddLoading() {
      if (!this.drawer || !window.matchMedia('(max-width: 768px)').matches) return false;
      this.pendingMobileAddLoads += 1;
      let loading = this.drawer.querySelector('.cart-slide-drawer__add-loading');
      if (!loading) {
        loading = document.createElement('div');
        loading.className = 'cart-slide-drawer__add-loading';
        loading.setAttribute('role', 'status');
        loading.setAttribute('aria-live', 'polite');
        loading.setAttribute('aria-atomic', 'true');
        loading.innerHTML = '<span class="cart-slide-drawer__spinner" aria-hidden="true"></span>';
        this.drawer.appendChild(loading);
      }
      loading.hidden = false;
      this.content?.setAttribute('aria-busy', 'true');
      if (!this.isOpen) this.open();
      return true;
    },

    endMobileAddLoading() {
      this.pendingMobileAddLoads = Math.max(0, this.pendingMobileAddLoads - 1);
      if (this.pendingMobileAddLoads > 0) return;
      const loading = this.drawer?.querySelector('.cart-slide-drawer__add-loading');
      if (loading) loading.hidden = true;
      this.content?.removeAttribute('aria-busy');
    },

    showLoading() {
      this.rememberComparePrices(this.itemsWrap);
      // The mobile add overlay keeps the current list stable until its replacement is ready.
      if (this.pendingMobileAddLoads > 0) return;
      if (this.itemsWrap) {
        this.itemsWrap.innerHTML = `
          <div class="cart-slide-drawer__loading">
            <div class="cart-slide-drawer__spinner"></div>
          </div>
        `;
      }
    },

    async renderCart(cart, sectionHtml, sequence = this.refreshSequence) {
      if (sequence !== this.refreshSequence) return;
      // 计算显示数量（bundle 按套计算）
      const displayCount = this.calculateDisplayCount(cart);
      
      // Update count (with parentheses to match HTML format)
      const countEl = document.getElementById('cart-slide-drawer-count');
      if (countEl) {
        countEl.textContent = '(' + displayCount + ')';
      }

      // 优先复用 Cart API bundled section rendering 返回的 HTML；只有旧调用方没有携带
      // section 时才单独请求，避免每次加购/改数量后固定多打一条 Section Rendering 请求。
      let sectionData = null;
      let useFallback = true;
      try {
        let html = sectionHtml;
        if (!html) {
          const response = await fetch('/?section_id=cart-slide-drawer-content');
          if (sequence !== this.refreshSequence) return;
          if (response.status === 429) {
            window.SicotasCartRateLimit?.notify(response);
            html = null;
          } else if (!response.ok) {
            throw new Error(`Cart drawer section request failed: ${response.status}`);
          } else {
            html = await response.text();
          }
        }
        if (sequence !== this.refreshSequence) return;
        
        // If section rendering is available, use it
        if (html && !html.includes('<!DOCTYPE')) {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = html;
          this.rememberComparePrices(tempDiv);
          
          // 读取 Liquid 计算好的数据
          const dataEl = tempDiv.querySelector('#cart-slide-drawer-data');
          if (dataEl) {
            sectionData = {
              correctSubtotal: parseInt(dataEl.dataset.correctSubtotal) || 0,
              discountAmount: parseInt(dataEl.dataset.discountAmount) || 0,
              youSaveAmount: parseInt(dataEl.dataset.youSaveAmount) || 0,
              totalPrice: parseInt(dataEl.dataset.totalPrice) || 0,
              itemCount: parseInt(dataEl.dataset.itemCount) || 0
            };
          }
          
          const newItemsWrap = tempDiv.querySelector('.cart-slide-drawer__items-wrap') ||
                            tempDiv.querySelector('#cart-slide-drawer-items-wrap');
          const sectionItemCount = sectionData ? sectionData.itemCount : null;
          const sectionHasItems = !!newItemsWrap?.querySelector(
            '.cart-slide-drawer__item, .cart-slide-drawer__bundle'
          );
          const sectionKeys = new Set(Array.from(
            newItemsWrap?.querySelectorAll('.cart-slide-drawer__item[data-key]') || [],
            item => item.dataset.key
          ));
          const sectionHasCurrentItems = cart.items.every(item =>
            item.properties?._bundle === 'true' ||
            item.properties?._bundle_type === 'main' ||
            sectionKeys.has(String(item.key))
          );
          const sectionMatchesCart =
            sectionItemCount === cart.item_count &&
            sectionHasCurrentItems &&
            (cart.item_count === 0 || sectionHasItems);

          // cart/add.js 的 bundled section 偶尔仍是写入前的空购物车 HTML。不能让它
          // 覆盖已经从 /cart.js 取得的写后快照，否则会出现 Cart (1) 但内容为空。
          if (sectionMatchesCart && newItemsWrap && this.itemsWrap) {
            this.itemsWrap.innerHTML = newItemsWrap.innerHTML;
            useFallback = false;
          } else {
            sectionData = null;
          }

          // cart 已是本轮刷新拿到的写后快照；这里再请求一次 /cart.js 只会制造加购瞬间的
          // 重复突发流量。Liquid 的 Section Rendering 仍负责渲染最新折扣/金额。
        }
      } catch (error) {
        if (sequence !== this.refreshSequence) return;
        sectionData = null;
      }
      if (useFallback) {
        // Cart AJAX omits compare-at prices. Resolve real variant metadata before
        // rendering, including when a stale bundled section has been rejected.
        await this.loadComparePrices(cart);
        if (sequence !== this.refreshSequence) return;
        this.renderCartItems(cart);
      }
      if (sequence !== this.refreshSequence) return;

      // Show/hide footer based on cart items
      const footer = document.getElementById('cart-slide-drawer-footer');
      if (footer) {
        // 优先使用 Section Rendering 的数据，否则用 cart.js 的数据
        const itemCount = sectionData ? sectionData.itemCount : cart.item_count;
        
        if (itemCount > 0) {
          footer.classList.remove('is-hidden');
          
          // 使用 Liquid 计算好的值（如果有），否则回退到 JS 计算
          let correctSubtotal, discountAmount, youSaveAmount, totalPrice;
          
          if (sectionData) {
            correctSubtotal = sectionData.correctSubtotal;
            discountAmount = sectionData.discountAmount;
            youSaveAmount = sectionData.youSaveAmount;
            totalPrice = sectionData.totalPrice;
          } else {
            // Fallback: use the same variant prices as the item rows.
            const calcResult = this.calculateCorrectTotals(cart);
            correctSubtotal = calcResult.correctSubtotal;
            discountAmount = cart.original_total_price - cart.total_price;
            youSaveAmount = Math.max(0, correctSubtotal - cart.total_price);
            totalPrice = cart.total_price;
          }

          footer.dataset.correctSubtotal = String(correctSubtotal);

          // 更新 Subtotal
          const subtotalPrice = document.getElementById('cart-slide-drawer-subtotal');
          if (subtotalPrice) {
            subtotalPrice.textContent = this.formatMoney(correctSubtotal);
          }
          
          // 更新 Total 行的价格
          const totalPriceEl = document.getElementById('cart-slide-drawer-total');
          if (totalPriceEl) {
            totalPriceEl.textContent = this.formatMoney(totalPrice);
          }
          // 更新 checkout 按钮上的价格
          const checkoutTotal = document.getElementById('cart-slide-drawer-checkout-total');
          if (checkoutTotal) {
            checkoutTotal.textContent = this.formatMoney(totalPrice);
          }
          
          // 更新 "Discount" 行
          let discountRow = document.getElementById('cart-slide-drawer-discount-row');
          if (discountRow) {
            if (discountAmount > 0) {
              discountRow.style.display = '';
              const discountValue = document.getElementById('cart-slide-drawer-discount-amount');
              if (discountValue) {
                discountValue.textContent = '-' + this.formatMoney(discountAmount);
              }
            } else {
              discountRow.style.display = 'none';
            }
          }
          
          this.updateSavingsAmount(youSaveAmount);
        } else {
          footer.classList.add('is-hidden');
        }
      }
      
      // 渲染折扣标签
      this.renderDiscountTags(cart);
      this.updateKlipAdjustedSummary(cart);
    },

    updateSavingsAmount(youSaveAmount) {
      const footer = document.getElementById('cart-slide-drawer-footer');
      if (!footer) return;
      let savingsRow = document.getElementById('cart-slide-drawer-savings');

      // 如果 savings-row 不存在但有折扣，则创建它
      if (!savingsRow && youSaveAmount > 0) {
        const totalRow = footer.querySelector('.cart-slide-drawer__total-row');
        if (totalRow) {
          savingsRow = document.createElement('div');
          savingsRow.className = 'cart-slide-drawer__savings-row';
          savingsRow.id = 'cart-slide-drawer-savings';
          savingsRow.innerHTML = `
            <span class="cart-slide-drawer__savings-label">You Save</span>
            <span class="cart-slide-drawer__savings-amount">-${this.formatMoney(youSaveAmount)}</span>
          `;
          totalRow.parentNode.insertBefore(savingsRow, totalRow);
        }
      } else if (savingsRow) {
        if (youSaveAmount > 0) {
          savingsRow.style.display = '';
          const savingsAmount = savingsRow.querySelector('.cart-slide-drawer__savings-amount');
          if (savingsAmount) {
            savingsAmount.textContent = '-' + this.formatMoney(youSaveAmount);
          }
        } else {
          savingsRow.style.display = 'none';
        }
      }
    },

    async getCartSnapshot(options) {
      if (window.SicotasCartSnapshot) {
        return window.SicotasCartSnapshot.get(options);
      }
      const response = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
      return response.json();
    },

    // 429/网络抖动这类瞬时问题几乎总能在几次退避重试内恢复，跟 variant-migration.js
    // 的 fetchWithRetry 用同样的思路（指数退避），避免单次重试固定延迟不够长导致放弃
    async getCartSnapshotWithRetry(maxAttempts) {
      maxAttempts = maxAttempts || 3;
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        if (window.SicotasCartRateLimit?.isCoolingDown()) return null;
        const cart = await this.getCartSnapshot();
        if (cart) return cart;
        if (attempt < maxAttempts) {
          const delayMs = 800 * Math.pow(2, attempt - 1); // 800ms, 1600ms
          await new Promise(resolve => setTimeout(resolve, delayMs));
        }
      }
      return null;
    },

    // 渲染折扣标签（显示折扣码名称+该商品的折扣金额）
    renderDiscountTags(cart) {
      console.log('🔍 renderDiscountTags called, cart.items:', cart.items.length);
      cart.items.forEach(item => {
        console.log('🔍 Item:', item.product_title, 'discounts:', item.discounts);
        if (item.discounts && item.discounts.length > 0) {
          const container = document.querySelector(`.cart-slide-drawer__discount-tags-container[data-item-key="${item.key}"]`);
          console.log('🔍 Container found:', !!container, 'for key:', item.key);
          if (container) {
            // 生成折扣标签HTML（只显示折扣码名称）
            const tagsHTML = item.discounts.map(discount => `
              <span class="cart-slide-drawer__discount-tag">
                <svg aria-hidden="true" focusable="false" viewBox="0 0 12 12" width="10" height="10">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M7 0h3a2 2 0 012 2v3a1 1 0 01-.3.7l-6 6a1 1 0 01-1.4 0l-4-4a1 1 0 010-1.4l6-6A1 1 0 017 0zm2 2a1 1 0 102 0 1 1 0 00-2 0z" fill="currentColor"></path>
                </svg>
                ${discount.title}
              </span>
            `).join('');
            
            // 计算折扣金额（使用discount.amount）
            const totalDiscountAmount = item.discounts.reduce((sum, d) => sum + d.amount, 0);
            
            // 计算折扣百分比
            let discountPercent = 0;
            if (item.original_line_price > 0) {
              discountPercent = Math.round((totalDiscountAmount / item.original_line_price) * 100);
            }
            
            // 生成折扣金额HTML（格式：(-10% Save $30.00)）
            const discountAmountHTML = totalDiscountAmount > 0 
              ? `<div class="cart-slide-drawer__discount-amount-text">(-${discountPercent}% Save ${this.formatMoney(totalDiscountAmount)})</div>`
              : '';
            
            container.innerHTML = tagsHTML + discountAmountHTML;
          }
        }
      });
      
      // 更新主商品模式的价格显示
      this.updateBundleMainPrices(cart);

      // 更新普通商品的价格显示（处理compare_at_price和自动折扣）
      this.updateNormalItemPrices(cart);

      // 渲染 Klip 优惠券折扣标签和最终价格
      this.renderKlipDiscounts(cart);
    },
    
    // 更新主商品模式套装的价格显示（扣除自动折扣）
    updateBundleMainPrices(cart) {
      cart.items.forEach(item => {
        // 检查是否是主商品模式
        const props = item.properties || {};
        if (props._bundle_type === 'main') {
          // 计算自动折扣总额
          const autoDiscountTotal = item.discounts?.reduce((sum, d) => sum + d.amount, 0) || 0;
          // 真正的最终价格 = final_line_price - 自动折扣
          const realFinalPrice = item.final_line_price - autoDiscountTotal;
          
          // 更新价格显示
          const priceElement = document.querySelector(`.cart-slide-drawer__bundle[data-key="${item.key}"] .cart-slide-drawer__bundle-total`);
          if (priceElement) {
            priceElement.textContent = this.formatMoney(realFinalPrice);
          }
        }
      });
    },
    
    // 更新普通商品的价格显示
    // 注意：划线价和零售价由 Liquid 模板处理，JS 只更新最终支付金额
    updateNormalItemPrices(cart) {
      cart.items.forEach(item => {
        const props = item.properties || {};
        // 跳过套装商品
        if (props._bundle === 'true' || props._bundle_type === 'main') {
          return;
        }
        
        const itemElement = document.querySelector(`.cart-slide-drawer__item[data-key="${item.key}"]`);
        if (!itemElement) return;
        
        // 查找最终支付金额元素（通过 data-item-key 匹配）
        const finalPriceElement = itemElement.querySelector(`.cart-slide-drawer__item-price--final[data-item-key="${item.key}"]`);
        if (finalPriceElement) {
          // 检查是否有折扣（discounts 数组）
          const hasDiscount = item.discounts && item.discounts.length > 0;
          
          if (hasDiscount) {
            // Cart API 有折扣数据，用 Cart API 值更新
            const totalDiscountAmount = item.discounts.reduce((sum, d) => sum + d.amount, 0);
            const actualFinalPrice = Math.round(item.original_line_price - totalDiscountAmount);
            finalPriceElement.textContent = this.formatMoney(actualFinalPrice);
            finalPriceElement.style.display = 'block';
          } else if (item.final_line_price < item.original_line_price) {
            // Cart API price 差值（非 discounts 数组触发的折扣）
            finalPriceElement.textContent = this.formatMoney(item.final_line_price);
            finalPriceElement.style.display = 'block';
          } else {
            // Cart API 无折扣数据：检查 Liquid 是否已通过 data-final-line 标记有折扣
            // 若 Liquid 渲染时 final < retail（如折扣码在 Liquid 层生效），保留 Liquid 的显示
            const liquidFinal = parseInt(finalPriceElement.dataset.finalLine || '0', 10);
            const liquidRetail = parseInt(finalPriceElement.dataset.retailLine || '0', 10);
            if (liquidFinal > 0 && liquidFinal < liquidRetail) {
              // Liquid 已正确渲染折扣码效果，不覆盖
            } else {
              finalPriceElement.style.display = 'none';
            }
          }
        }
      });
    },
    
    // 渲染 Klip 优惠券折扣标签和最终价格（基于 cart.attributes.klipC）
    renderKlipDiscounts(cart) {
      const klipCStr = cart.attributes && cart.attributes.klipC;
      if (!klipCStr) return;

      let klipEntries;
      try { klipEntries = JSON.parse(klipCStr); } catch (e) { return; }
      if (!Array.isArray(klipEntries) || klipEntries.length === 0) return;

      // 只保留 isValid=true 的条目，按 vid 去重（同一 variant 取第一个有效条目）
      const klipMap = {};
      klipEntries.forEach(entry => {
        if (entry.isValid && entry.vid && !klipMap[entry.vid]) {
          klipMap[entry.vid] = entry;
        }
      });

      cart.items.forEach(item => {
        const entry = klipMap[item.variant_id];
        if (!entry) return;
        if (!this._isKlipEntryEligibleForCurrentPage(item, entry)) return;

        // 跳过 bundle 子商品（_bundle: true），但保留 bundle 主商品（_bundle_type: main）
        const props = item.properties || {};
        if (props._bundle === 'true') return;

        const container = document.querySelector(`.cart-slide-drawer__discount-tags-container[data-item-key="${item.key}"]`);
        if (!container) return;

        // 解析折扣值（支持百分比如 "20%"，或固定金额如 "10"）
        const dvStr = String(entry.dv || '0');
        let discountPct = 0;
        let discountAmount = 0;

        if (dvStr.endsWith('%')) {
          discountPct = parseFloat(dvStr);
          discountAmount = Math.round(item.original_line_price * discountPct / 100);
        } else {
          // 固定金额（单位：元），需转为分
          const dvCents = Math.round(parseFloat(dvStr) * 100) * item.quantity;
          discountAmount = dvCents;
          discountPct = item.original_line_price > 0
            ? Math.round(dvCents / item.original_line_price * 100)
            : 0;
        }

        const finalPrice = item.original_line_price - discountAmount;
        const title = entry.t || '';

        // 追加 Klip 折扣标签（不清空容器，与 Shopify 原生折扣标签共存）
        // JS 渲染的标签用 data-klip-title 属性匹配；Liquid 渲染的标签（无该属性）用 textContent 匹配
        // （SVG <path> 无文本内容，textContent.trim() 结果即为标签文字，匹配可靠）
        const escapedTitle = title.replace(/"/g, '\\"');
        const alreadyHasTag =
          container.querySelector(`.cart-slide-drawer__discount-tag[data-klip-title="${escapedTitle}"]`) ||
          [...container.querySelectorAll('.cart-slide-drawer__discount-tag:not([data-klip-title])')].some(el => el.textContent.trim() === title.trim());
        if (!alreadyHasTag) {
          const tagSpan = document.createElement('span');
          tagSpan.className = 'cart-slide-drawer__discount-tag';
          tagSpan.dataset.klipTitle = title;
          // SVG 用 innerHTML 注入，title 用 textContent 追加，避免 XSS
          tagSpan.innerHTML = '<svg aria-hidden="true" focusable="false" viewBox="0 0 12 12" width="10" height="10"><path fill-rule="evenodd" clip-rule="evenodd" d="M7 0h3a2 2 0 012 2v3a1 1 0 01-.3.7l-6 6a1 1 0 01-1.4 0l-4-4a1 1 0 010-1.4l6-6A1 1 0 017 0zm2 2a1 1 0 102 0 1 1 0 00-2 0z" fill="currentColor"></path></svg>';
          tagSpan.appendChild(document.createTextNode(title));
          container.appendChild(tagSpan);
        }
        // 仅在容器内没有 amount-text 时写入（Shopify 折扣已显示 amount 则不覆盖）
        if (discountAmount > 0 && !container.querySelector('.cart-slide-drawer__discount-amount-text')) {
          const amountDiv = document.createElement('div');
          amountDiv.className = 'cart-slide-drawer__discount-amount-text';
          amountDiv.textContent = `(-${discountPct}% Save ${this.formatMoney(discountAmount)})`;
          container.appendChild(amountDiv);
        }

        // 显示最终价格（覆盖 updateNormalItemPrices 的 hide 决定）
        const finalPriceEl = document.querySelector(`.cart-slide-drawer__item-price--final[data-item-key="${item.key}"]`);
        if (finalPriceEl && finalPrice > 0 && finalPrice < item.original_line_price) {
          finalPriceEl.textContent = this.formatMoney(finalPrice);
          finalPriceEl.style.display = 'block';
        }
      });
    },

    // 用页面已有的 window.KLIP_APP_DATA 立即渲染 Klip 折扣标签，无需等待 cart.attributes.klipC
    renderKlipDiscountsFromPageData(cart, options = {}) {
      const coupons = window.KLIP_APP_DATA?.coupons;
      if (!Array.isArray(coupons) || coupons.length === 0) return;

      const activeCoupons = coupons.filter(c => c.published && c.autoClipped);
      const preferredCoupon = options.preferredCouponId
        ? coupons.find(coupon => coupon.published && String(coupon.id) === String(options.preferredCouponId))
        : null;
      if (activeCoupons.length === 0 && !preferredCoupon) return;

      const targetItemKeys = options.itemKeys instanceof Set ? options.itemKeys : null;
      const targetVariantIds = options.variantIds instanceof Set ? options.variantIds : null;

      cart.items.forEach(item => {
        const props = item.properties || {};
        if (props._bundle === 'true') return;
        const hasTargetItems = !!(targetItemKeys?.size || targetVariantIds?.size);
        const matchesAddedItem =
          targetItemKeys?.has(String(item.key)) ||
          targetVariantIds?.has(String(item.variant_id));
        if (hasTargetItems && !matchesAddedItem) return;

        const container = document.querySelector(`.cart-slide-drawer__discount-tags-container[data-item-key="${item.key}"]`);
        if (!container) return;

        // renderDiscountTags 已经用 cart.attributes.klipC（Klip 已经算好的权威结果）渲染过
        // 一次折扣标签；这里只是在权威数据还没写入前，用页面配置先猜一个近似结果，如果
        // 容器里已经有真实标签了就不需要再猜，避免猜测结果和真实结果同时显示
        if (container.querySelector('.cart-slide-drawer__discount-tag')) return;

        // 同一行商品实际只会享受一个 Klip 自动折扣（与 renderKlipDiscounts 按 vid 去重取
        // 一条的语义一致）。多个优惠券的 target 规则同时命中同一商品时（比如同时上线两档
        // 力度不同的活动码），无法从页面配置判断 Klip 最终会应用哪一个，只能猜折扣力度更
        // 大的那个，不能把所有命中的都渲染成标签——那会让页面显示两个优惠券同时生效
        let bestCoupon = null;
        let bestDiscountAmount = -1;
        let bestDiscountPct = 0;
        // 加购事件带有明确 coupon ID 时只能使用该优惠，不能再从页面其它自动优惠中
        // 挑一个力度更大的，否则会给当前商品展示并未选中的优惠券。
        const couponCandidates = preferredCoupon ? [preferredCoupon] : [...activeCoupons];
        couponCandidates.forEach(coupon => {
          if (!this._klipCouponAppliesTo(coupon, item, { claimed: Boolean(preferredCoupon) })) return;

          const disc = coupon.discount || {};
          let discountPct = 0;
          let discountAmount = 0;

          if (disc.type === 'percentage') {
            discountPct = disc.value || 0;
            discountAmount = Math.round(item.original_line_price * discountPct / 100);
          } else {
            const dvCents = Math.round((disc.value || 0) * 100) * item.quantity;
            discountAmount = dvCents;
            discountPct = item.original_line_price > 0
              ? Math.round(dvCents / item.original_line_price * 100)
              : 0;
          }

          if (discountAmount > bestDiscountAmount) {
            bestDiscountAmount = discountAmount;
            bestDiscountPct = discountPct;
            bestCoupon = coupon;
          }
        });
        if (!bestCoupon || bestDiscountAmount <= 0) return;

        const title = bestCoupon.title || '';
        const finalPrice = item.original_line_price - bestDiscountAmount;
        const escapedTitle = title.replace(/"/g, '\\"');
        const alreadyHasTag =
          container.querySelector(`.cart-slide-drawer__discount-tag[data-klip-title="${escapedTitle}"]`) ||
          [...container.querySelectorAll('.cart-slide-drawer__discount-tag:not([data-klip-title])')].some(el => el.textContent.trim() === title.trim());

        if (!alreadyHasTag) {
          const tagSpan = document.createElement('span');
          tagSpan.className = 'cart-slide-drawer__discount-tag';
          tagSpan.dataset.klipTitle = title;
          tagSpan.innerHTML = '<svg aria-hidden="true" focusable="false" viewBox="0 0 12 12" width="10" height="10"><path fill-rule="evenodd" clip-rule="evenodd" d="M7 0h3a2 2 0 012 2v3a1 1 0 01-.3.7l-6 6a1 1 0 01-1.4 0l-4-4a1 1 0 010-1.4l6-6A1 1 0 017 0zm2 2a1 1 0 102 0 1 1 0 00-2 0z" fill="currentColor"></path></svg>';
          tagSpan.appendChild(document.createTextNode(title));
          container.appendChild(tagSpan);
        }

        if (bestDiscountAmount > 0 && !container.querySelector('.cart-slide-drawer__discount-amount-text')) {
          const amountDiv = document.createElement('div');
          amountDiv.className = 'cart-slide-drawer__discount-amount-text';
          amountDiv.textContent = `(-${bestDiscountPct}% Save ${this.formatMoney(bestDiscountAmount)})`;
          container.appendChild(amountDiv);
        }

        const finalPriceEl = document.querySelector(`.cart-slide-drawer__item-price--final[data-item-key="${item.key}"]`);
        if (finalPriceEl && finalPrice > 0 && finalPrice < item.original_line_price) {
          finalPriceEl.textContent = this.formatMoney(finalPrice);
          finalPriceEl.style.display = 'block';
        }
      });
    },

    _klipTargetMatches(target, item) {
      if (!target) return false;
      const targetType = target.type || target.mode;
      if (targetType === 'allProducts') return true;

      const selectedItems = Array.isArray(target.selectedItems) ? target.selectedItems : [];
      const productId = String(item.product_id || '');
      const variantId = String(item.variant_id || '');

      if (targetType === 'products') {
        const productIds = selectedItems
          .map(selected => selected.id || selected.product_id || selected.productId)
          .concat(target.productIds || []);
        return productIds.some(id => String(id) === productId);
      }

      if (targetType === 'variants') {
        const variantIds = selectedItems
          .map(selected => selected.variantId || selected.variant_id || selected.id)
          .concat(target.variantIds || []);
        return variantIds.some(id => String(id) === variantId);
      }

      const pageProduct = window.KLIP_APP_DATA?.product;
      const productData = window.KLIP_APP_DATA?.products?.[productId] ||
        (String(pageProduct?.id || '') === productId ? pageProduct : {});

      if (targetType === 'collections') {
        const collectionIds = new Set((productData.collectionIds || []).map(String));
        const targetCollectionIds = selectedItems
          .map(selected => selected.id || selected.collection_id || selected.collectionId)
          .concat(target.collectionIds || []);
        return targetCollectionIds.some(id => collectionIds.has(String(id)));
      }

      if (targetType === 'tags') {
        const productTags = new Set((productData.tags || []).map(tag => String(tag).trim().toLowerCase()));
        const targetTags = Array.isArray(target.tags)
          ? target.tags
          : String(target.tags || '').split(',');
        return targetTags
          .map(tag => String(tag).trim().toLowerCase())
          .filter(Boolean)
          .some(tag => productTags.has(tag));
      }

      if (targetType === 'selectedItems') {
        return selectedItems.some(selected =>
          String(selected.id || selected.product_id || selected.productId || '') === productId ||
          String(selected.variant_id || selected.variantId || '') === variantId
        );
      }
      return false;
    },

    // 判断 Klip 优惠券是否适用于指定商品，并遵守优惠券的排除范围。
    _klipCouponAppliesTo(coupon, item, { claimed = false } = {}) {
      if (!coupon?.published || coupon.type !== 'line_item') return false;
      if (coupon.visibility?.productPage === false) return false;
      if (!claimed) {
        if (!coupon.autoClipped) return false;
        if (coupon.discountMethod && coupon.discountMethod !== 'automatic') return false;
        if (coupon.customerLoginRequired && !window.KLIP_APP_DATA?.loggedIn) return false;
        if (coupon.onePerCustomer || coupon.leadCapture?.enabled || coupon.spend?.enabled) return false;
        if (
          coupon.marketingTarget?.utm?.utmTargeting ||
          coupon.marketingTarget?.location?.enabled ||
          coupon.marketingTarget?.customer?.enabled
        ) return false;
        if (coupon.discountConditions?.OR?.length) return false;
        if (coupon.usageLimit && Number(coupon.usage || 0) >= Number(coupon.usageLimit)) return false;
      }

      const now = Date.now();
      const startsAt = coupon.startDate ? Date.parse(coupon.startDate) : NaN;
      const endsAt = coupon.endDate ? Date.parse(coupon.endDate) : NaN;
      if (Number.isFinite(startsAt) && startsAt > now) return false;
      if (Number.isFinite(endsAt) && endsAt < now) return false;

      const target = coupon.target;
      if (!this._klipTargetMatches(target, item)) return false;
      const excluded = target?.exclude;
      return !(excluded?.enabled && this._klipTargetMatches(excluded, item));
    },

    _isKlipEntryEligibleForCurrentPage(item, entry) {
      const pageProductId = String(window.KLIP_APP_DATA?.product?.id || '');
      if (!pageProductId || String(item.product_id) !== pageProductId) return true;

      const coupons = window.KLIP_APP_DATA?.coupons;
      if (!Array.isArray(coupons) || !entry?.cid) return true;
      const coupon = coupons.find(candidate => String(candidate.id) === String(entry.cid));
      return coupon ? this._klipCouponAppliesTo(coupon, item, { claimed: true }) : true;
    },

    _getKlipEntryDiscount(item, entry) {
      if (!entry || entry.isValid === false) return 0;

      const discountValue = String(entry.dv || '0');
      if (discountValue.endsWith('%')) {
        const percentage = parseFloat(discountValue);
        return Number.isFinite(percentage)
          ? Math.round(item.original_line_price * percentage / 100)
          : 0;
      }

      const fixedAmount = parseFloat(discountValue);
      return Number.isFinite(fixedAmount)
        ? Math.round(fixedAmount * 100) * item.quantity
        : 0;
    },

    _getPageCouponDiscount(item, options = {}) {
      const coupons = window.KLIP_APP_DATA?.coupons;
      if (!Array.isArray(coupons) || coupons.length === 0) return 0;

      const targetItemKeys = options.itemKeys instanceof Set ? options.itemKeys : null;
      const targetVariantIds = options.variantIds instanceof Set ? options.variantIds : null;
      const hasTargets = !!(targetItemKeys?.size || targetVariantIds?.size);
      const isTarget =
        targetItemKeys?.has(String(item.key)) ||
        targetVariantIds?.has(String(item.variant_id));
      if (hasTargets && !isTarget) return 0;

      const preferredCoupon = options.preferredCouponId
        ? coupons.find(coupon =>
            coupon.published && String(coupon.id) === String(options.preferredCouponId)
          )
        : null;
      const candidates = preferredCoupon
        ? [preferredCoupon]
        : coupons.filter(coupon => coupon.published && coupon.autoClipped);

      return candidates.reduce((bestDiscount, coupon) => {
        if (!this._klipCouponAppliesTo(coupon, item, { claimed: Boolean(preferredCoupon) })) return bestDiscount;

        const discount = coupon.discount || {};
        const amount = discount.type === 'percentage'
          ? Math.round(item.original_line_price * (parseFloat(discount.value) || 0) / 100)
          : Math.round((parseFloat(discount.value) || 0) * 100) * item.quantity;
        return Math.max(bestDiscount, amount);
      }, 0);
    },

    _buildKlipEntry(coupon, item) {
      const discount = coupon?.discount || {};
      const discountValue = discount.type === 'percentage'
        ? `${parseFloat(discount.value) || 0}%`
        : String(parseFloat(discount.value) || 0);

      return {
        dv: discountValue,
        dm: discount.max || null,
        dc: coupon.discountConditions || { OR: [] },
        t: coupon.code || coupon.discountCode || coupon.title || '',
        l: null,
        li: null,
        tp: coupon.type || 'line_item',
        cid: coupon.id,
        vid: Number(item.variant_id),
        pid: Number(item.product_id),
        isValid: true
      };
    },

    async persistKlipCouponForAddedItems(addedProduct, couponId, previousEntries = [], productData = null) {
      if (!couponId || !addedProduct) return null;

      const coupons = window.KLIP_APP_DATA?.coupons;
      if (!Array.isArray(coupons)) return null;
      const coupon = coupons.find(item =>
        item?.published && String(item.id) === String(couponId)
      );
      if (!coupon) return null;

      const addedItems = Array.isArray(addedProduct.items)
        ? addedProduct.items
        : [addedProduct];
      const addedKeys = new Set(
        addedItems.map(item => String(item?.key || '')).filter(Boolean)
      );
      const addedVariantIds = new Set(
        addedItems
          .map(item => String(item?.variant_id || item?.id || ''))
          .filter(Boolean)
      );

      const cart = await this.getCartSnapshot({ force: true });
      if (!cart) return null;
      const targetItems = cart.items.filter(item =>
        addedKeys.has(String(item.key)) || addedVariantIds.has(String(item.variant_id))
      );
      if (!targetItems.length) return cart;

      // Klip can rebuild its global data while add.js is in flight. Restore the
      // submitting product's targeting data, never the host page's product.
      if (productData?.id && targetItems.some(item => String(item.product_id) === String(productData.id))) {
        window.KLIP_APP_DATA.products = window.KLIP_APP_DATA.products || {};
        window.KLIP_APP_DATA.products[String(productData.id)] = productData;
      }

      let entries = [];
      try {
        entries = JSON.parse(cart.attributes?.klipC || '[]');
      } catch (error) {
        entries = [];
      }
      if (!Array.isArray(entries)) entries = [];

      // cart.attributes.klipC 是一个整体字符串。Klip 在第二次加购时可能只写入本次商品，
      // 因此必须把加购前快照与写入后结果合并；当前结果优先，以便保留 Klip 对同一商品
      // 的最新校验状态，同时不丢掉仍在购物车里的旧商品优惠。
      const cartVariantIds = new Set(cart.items.map(item => String(item.variant_id)));
      const entriesByVariant = new Map();
      previousEntries
        .filter(entry => entry?.vid && cartVariantIds.has(String(entry.vid)))
        .forEach(entry => entriesByVariant.set(String(entry.vid), entry));
      entries
        .filter(entry => entry?.vid && cartVariantIds.has(String(entry.vid)))
        .forEach(entry => entriesByVariant.set(String(entry.vid), entry));
      const currentEntriesByVariant = new Map(
        entries
          .filter(entry => entry?.vid && cartVariantIds.has(String(entry.vid)))
          .map(entry => [String(entry.vid), entry])
      );
      let changed = entriesByVariant.size !== currentEntriesByVariant.size ||
        [...entriesByVariant].some(([variantId, entry]) =>
          JSON.stringify(currentEntriesByVariant.get(variantId) || null) !== JSON.stringify(entry)
        );

      targetItems.forEach(item => {
        if (item.properties?._bundle === 'true') return;
        if (!this._klipCouponAppliesTo(coupon, item, { claimed: true })) return;

        const nextEntry = this._buildKlipEntry(coupon, item);
        const currentEntry = entriesByVariant.get(String(item.variant_id));
        if (JSON.stringify(currentEntry || null) !== JSON.stringify(nextEntry)) {
          entriesByVariant.set(String(item.variant_id), nextEntry);
          changed = true;
        }
      });

      if (!changed) return cart;

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Sicotas-Klip-Sync': '1'
        },
        body: JSON.stringify({
          attributes: {
            klipC: JSON.stringify(Array.from(entriesByVariant.values()))
          },
          sections: ['cart-slide-drawer-content'],
          sections_url: window.location.pathname
        })
      });
      if (!response.ok) {
        throw new Error(`Klip cart attribute update failed: ${response.status}`);
      }
      return response.json();
    },

    calculateKlipAdjustedTotal(cart, options = {}) {
      let klipEntries = [];
      try {
        klipEntries = JSON.parse(cart.attributes?.klipC || '[]');
      } catch (error) {
        klipEntries = [];
      }

      const entriesByVariant = new Map();
      if (Array.isArray(klipEntries)) {
        klipEntries.forEach(entry => {
          if (entry?.isValid && entry.vid && !entriesByVariant.has(String(entry.vid))) {
            entriesByVariant.set(String(entry.vid), entry);
          }
        });
      }

      let extraKlipDiscount = 0;
      const canUsePageCoupon =
        options.itemKeys instanceof Set ||
        options.variantIds instanceof Set ||
        !!options.preferredCouponId;
      cart.items.forEach(item => {
        if (item.properties?._bundle === 'true') return;

        const entry = entriesByVariant.get(String(item.variant_id));
        if (entry && !this._isKlipEntryEligibleForCurrentPage(item, entry)) return;
        const klipDiscount = entry
          ? this._getKlipEntryDiscount(item, entry)
          : canUsePageCoupon
            ? this._getPageCouponDiscount(item, options)
            : 0;
        if (klipDiscount <= 0) return;

        // Shopify 已计入的 line/cart discount 不应被 Klip 重复扣减。
        // 只补上 Klip 目标价低于当前行实付价的差额。
        const klipFinalLine = Math.max(0, item.original_line_price - klipDiscount);
        extraKlipDiscount += Math.max(0, item.final_line_price - klipFinalLine);
      });

      return Math.max(0, cart.total_price - extraKlipDiscount);
    },

    getKlipCheckoutDiscountCodes(cart, options = {}) {
      let klipEntries = [];
      try {
        klipEntries = JSON.parse(cart.attributes?.klipC || '[]');
      } catch (error) {
        klipEntries = [];
      }

      const cartItemsByVariant = new Map(
        cart.items.map(item => [String(item.variant_id), item])
      );
      const codes = Array.isArray(klipEntries)
        ? klipEntries
            .filter(entry => {
              if (!entry?.isValid || !entry.t || !entry.vid) return false;
              const item = cartItemsByVariant.get(String(entry.vid));
              return !!item && this._isKlipEntryEligibleForCurrentPage(item, entry);
            })
            .map(entry => String(entry.t).trim())
            .filter(Boolean)
        : [];

      if (options.preferredCouponId) {
        const coupons = window.KLIP_APP_DATA?.coupons;
        const coupon = Array.isArray(coupons)
          ? coupons.find(item =>
              item?.published && String(item.id) === String(options.preferredCouponId)
            )
          : null;
        const preferredCode = String(
          coupon?.code || coupon?.discountCode || coupon?.title || ''
        ).trim();
        if (preferredCode) codes.push(preferredCode);
      }

      // Shopify checkout 最多接受 5 个商品/订单优惠码。保持购物车条目顺序并去重。
      return [...new Set(codes)].slice(0, 5);
    },

    updateKlipAdjustedSummary(cart, options = {}) {
      const adjustedTotal = this.calculateKlipAdjustedTotal(cart, options);
      const checkoutButton = this.drawer?.querySelector('.cart-slide-drawer__checkout-btn');
      const discountCodes = this.getKlipCheckoutDiscountCodes(cart, options);
      const canApplyAtCheckout = adjustedTotal < cart.total_price && discountCodes.length > 0;
      if (checkoutButton) {
        if (canApplyAtCheckout) {
          checkoutButton.dataset.discountCodes = discountCodes.join(',');
        } else {
          checkoutButton.removeAttribute('data-discount-codes');
        }
      }
      // 没有可传给 Shopify checkout 的真实优惠码时，不展示前端预估价。
      if (!canApplyAtCheckout) return;

      const totalPriceEl = document.getElementById('cart-slide-drawer-total');
      const checkoutTotal = document.getElementById('cart-slide-drawer-checkout-total');
      if (totalPriceEl) totalPriceEl.textContent = this.formatMoney(adjustedTotal);
      if (checkoutTotal) checkoutTotal.textContent = this.formatMoney(adjustedTotal);

      // Discount 保持零售价到实付价的优惠，You Save 汇总全部优惠。
      const discountAmount = Math.max(0, cart.original_total_price - adjustedTotal);
      const discountRow = document.getElementById('cart-slide-drawer-discount-row');
      const discountValue = document.getElementById('cart-slide-drawer-discount-amount');
      if (discountRow) discountRow.style.display = discountAmount > 0 ? '' : 'none';
      if (discountValue) discountValue.textContent = '-' + this.formatMoney(discountAmount);

      const footer = document.getElementById('cart-slide-drawer-footer');
      const correctSubtotal = Number(footer?.dataset.correctSubtotal ?? this.calculateCorrectTotals(cart).correctSubtotal);
      this.updateSavingsAmount(Math.max(0, correctSubtotal - adjustedTotal));
    },

    rememberComparePrices(root) {
      root?.querySelectorAll('[data-variant-id][data-compare-at-price]').forEach(element => {
        const price = Number(element.dataset.compareAtPrice);
        if (Number.isFinite(price)) this.variantComparePrices.set(String(element.dataset.variantId), price);
      });
    },

    getCompareAtPrice(item) {
      const variantId = String(item.variant_id || item.id);
      const appData = window.KLIP_APP_DATA;
      const product = appData?.products?.[item.product_id] ||
        (String(appData?.product?.id) === String(item.product_id) ? appData.product : null);
      const variant = item.variant || product?.variants?.find(variant => String(variant.id) === variantId);
      // A missing field differs from an explicit null (a variant with no sale).
      if (variant && variant.compare_at_price !== undefined) {
        return Number(variant.compare_at_price) || 0;
      }
      return this.variantComparePrices.get(variantId);
    },

    async loadComparePrices(cart) {
      await Promise.all(cart.items.map(async item => {
        if (item.properties?._bundle === 'true' || this.getCompareAtPrice(item) !== undefined) return;
        const url = new URL(item.url || `/products/${item.handle || ''}`, window.location.origin);
        if (url.origin !== window.location.origin || !/\/products\/[^/]+$/.test(url.pathname)) return;
        url.pathname += '.js';
        url.search = '';
        url.hash = '';
        const key = url.href;
        if (!this.productPriceRequests.has(key)) {
          const request = (async () => {
            const controller = new AbortController();
            const timeout = window.setTimeout(() => controller.abort(), 3000);
            try {
              const response = await fetch(key, { credentials: 'same-origin', signal: controller.signal });
              if (!response.ok) throw new Error(`Product price request failed: ${response.status}`);
              const product = await response.json();
              for (const variant of product.variants || []) {
                if (variant.compare_at_price !== undefined) {
                  this.variantComparePrices.set(String(variant.id), Number(variant.compare_at_price) || 0);
                }
              }
            } finally {
              window.clearTimeout(timeout);
              this.productPriceRequests.delete(key);
            }
          })();
          this.productPriceRequests.set(key, request);
        }
        // A metadata outage must not prevent the cart from opening.
        try { await this.productPriceRequests.get(key); } catch (error) { /* Keep retail and final prices. */ }
      }));
    },

    // 计算正确的 Subtotal 和 Total（考虑套餐的真正划线价）
    calculateCorrectTotals(cart) {
      let correctSubtotal = 0;
      let correctTotal = 0;
      const processedBundleIds = new Set();
      
      for (const item of cart.items) {
        const props = item.properties || {};
        const bundleId = props._bundle_id;
        const isBundle = props._bundle === 'true';
        
        if (isBundle && bundleId) {
          // 套餐商品：检查是否已处理过这个 bundle
          if (!processedBundleIds.has(bundleId)) {
            // 收集这个 bundle 的信息
            let bundleMainComparePrice = 0;
            let bundleMainPrice = 0;
            let bundleTotalPrice = 0;
            let bundleQuantity = 1;
            
            for (const bundleItem of cart.items) {
              const biProps = bundleItem.properties || {};
              if (biProps._bundle_id === bundleId) {
                bundleTotalPrice += bundleItem.final_line_price;
                if (biProps._bundle_index === '0') {
                  bundleQuantity = bundleItem.quantity;
                }
                if (!bundleMainComparePrice && biProps._bundle_main_compare_price) {
                  bundleMainComparePrice = parseFloat(biProps._bundle_main_compare_price) || 0;
                }
                if (!bundleMainPrice && biProps._bundle_main_price) {
                  bundleMainPrice = parseFloat(biProps._bundle_main_price) || 0;
                }
              }
            }
            
            // 计算套餐的正确原价（使用 Math.round 确保整数 cents）
            let bundleOriginal = 0;
            if (bundleMainComparePrice > 0) {
              bundleOriginal = Math.round(bundleMainComparePrice * bundleQuantity);
            } else if (bundleMainPrice > 0) {
              bundleOriginal = Math.round(bundleMainPrice * bundleQuantity);
            }
            
            correctSubtotal += bundleOriginal;
            correctTotal += bundleTotalPrice;
            processedBundleIds.add(bundleId);
          }
        } else {
          const variantCompareAt = this.getCompareAtPrice(item);
          if (variantCompareAt && variantCompareAt > item.price) {
            // compare_at_price 是单价，需要乘以数量（使用 Math.round 确保整数 cents）
            correctSubtotal += Math.round(variantCompareAt * item.quantity);
          } else {
            // 没有 compare_at_price，使用 original_line_price（已经是行价格）
            correctSubtotal += item.original_line_price;
          }
          correctTotal += item.final_line_price;
        }
      }
      
      return { correctSubtotal, correctTotal };
    },

    renderCartItems(cart) {
      if (!this.itemsWrap) return;

      if (cart.item_count === 0) {
        this.itemsWrap.innerHTML = `
          <div class="cart-slide-drawer__empty">
            <h2 class="cart-slide-drawer__empty-title">Hi! Your shopping cart is empty.</h2>
            <p class="cart-slide-drawer__empty-text">If you have an account, sign in to see items added on earlier visits</p>
            <div class="cart-slide-drawer__empty-actions">
              <a href="/account/login" class="cart-slide-drawer__empty-btn cart-slide-drawer__empty-btn--login">Sign In</a>
              <a href="/collections/all" class="cart-slide-drawer__empty-btn cart-slide-drawer__empty-btn--continue">Continue Shopping</a>
            </div>
          </div>
        `;
        return;
      }

      // 收集所有 bundle_id 并去重
      const renderedBundleIds = new Set();
      let itemsHtml = '<ul class="cart-slide-drawer__items" id="cart-slide-drawer-items">';
      
      cart.items.forEach((item, index) => {
        const line = index + 1;
        const isBundle = item.properties && item.properties._bundle === 'true';
        const bundleId = item.properties ? item.properties._bundle_id : null;
        
        if (isBundle && bundleId) {
          // 如果已渲染过这个 bundle，跳过
          if (renderedBundleIds.has(bundleId)) return;
          renderedBundleIds.add(bundleId);
          
          // 渲染 bundle
          itemsHtml += this.renderBundleItem(cart, bundleId);
        } else {
          // 渲染普通商品
          itemsHtml += this.renderNormalItem(item, line);
        }
      });

      itemsHtml += '</ul>';
      this.itemsWrap.innerHTML = itemsHtml;
    },

    renderNormalItem(item, line) {
      const imageUrl = item.image ? item.image.replace(/(\.[^.]+)$/, '_160x$1') : '';
      const compareAt = this.getCompareAtPrice(item) || 0;
      const compareAtLine = Math.round(compareAt * item.quantity);
      const hasCompareAt = compareAt > item.price;
      
      // Cart quantity belongs to the stepper; only actual variant options go here.
      let optionsHtml = `<div class="cart-slide-drawer__item-options">`;
      if (item.variant_title && item.variant_title !== 'Default Title') {
        // 解析 options_with_values，显示所有选项
        if (item.options_with_values && item.options_with_values.length > 0) {
          [...item.options_with_values].reverse().forEach((opt) => {
            optionsHtml += `<div class="cart-slide-drawer__item-option">${this.escapeHtml(opt.name)}: ${this.escapeHtml(opt.value)}</div>`;
          });
        } else {
          optionsHtml += `<div class="cart-slide-drawer__item-option">${this.escapeHtml(item.variant_title)}</div>`;
        }
      }
      optionsHtml += `</div>`;

      return `
        <li class="cart-slide-drawer__item" data-line="${line}" data-key="${item.key}">
          <div class="cart-slide-drawer__item-image">
            ${imageUrl ? `<img src="${imageUrl}" alt="${this.escapeHtml(item.title)}" loading="lazy">` : ''}
          </div>
          <div class="cart-slide-drawer__item-details">
            <div class="cart-slide-drawer__item-row">
              <div class="cart-slide-drawer__item-info">
                <div class="cart-slide-drawer__item-title">${this.escapeHtml(item.product_title)}</div>
                ${optionsHtml}
              </div>
              <button type="button" class="cart-slide-drawer__item-remove" data-action="remove" data-line="${line}" aria-label="Remove">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clip-path="url(#clip0_remove_${line})">
                    <path d="M3 3.33333V14.6667H13V3.33333H3Z" stroke="#666666" stroke-width="1.2" stroke-linejoin="round"/>
                    <path d="M6.66675 6.66667V11" stroke="#666666" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M9.33325 6.66667V11" stroke="#666666" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M1.33325 3.33333H14.6666" stroke="#666666" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M5.33325 3.33333L6.42959 1.33333H9.59229L10.6666 3.33333H5.33325Z" stroke="#666666" stroke-width="1.2" stroke-linejoin="round"/>
                  </g>
                  <defs>
                    <clipPath id="clip0_remove_${line}">
                      <rect width="16" height="16" fill="white"/>
                    </clipPath>
                  </defs>
                </svg>
              </button>
            </div>
            <div class="cart-slide-drawer__item-meta-row">
              <div class="cart-slide-drawer__item-qty-col">
                <div class="cart-slide-drawer__item-qtywrap">
                  <button type="button" class="cart-slide-drawer__quantity-btn${item.quantity <= 1 ? ' is-disabled' : ''}" data-action="decrease" data-line="${line}" aria-label="Decrease quantity"${item.quantity <= 1 ? ' disabled' : ''}>
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M0.599609 4.9751H9.34961" stroke="#666666" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </button>
                  <input type="number" class="cart-slide-drawer__quantity-input" data-line="${line}" value="${item.quantity}" min="1" max="30" aria-label="Quantity">
                  <button type="button" class="cart-slide-drawer__quantity-btn${item.quantity >= 30 ? ' is-disabled' : ''}" data-action="increase" data-line="${line}" aria-label="Increase quantity"${item.quantity >= 30 ? ' disabled' : ''}>
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4.99581 0.600098L4.98438 9.3501" stroke="#666666" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M0.599609 4.9751H9.34961" stroke="#666666" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </button>
                </div>
              </div>
              <div class="cart-slide-drawer__item-price-wrap" data-item-key="${item.key}">
                <div class="cart-slide-drawer__item-price-row">
                  ${hasCompareAt ? `<span class="cart-slide-drawer__item-price-original cart-slide-drawer__item-price-original--fixed">${this.formatMoney(compareAtLine)}</span>` : ''}
                  <span class="cart-slide-drawer__item-price">${this.formatMoney(item.original_line_price)}</span>
                </div>
                ${hasCompareAt ? `<div class="cart-slide-drawer__item-discount">(Save ${this.formatMoney(compareAtLine - item.original_line_price)})</div>` : ''}
                <div class="cart-slide-drawer__discount-tags-container" data-item-key="${item.key}"></div>
                <span class="cart-slide-drawer__item-price cart-slide-drawer__item-price--final"
                      data-item-key="${item.key}"
                      data-retail-line="${item.original_line_price}"
                      data-final-line="${item.final_line_price}"
                      style="margin-top: 8px; display: ${item.original_line_price !== item.final_line_price ? 'block' : 'none'};">
                  ${this.formatMoney(item.final_line_price)}
                </span>
              </div>
            </div>
          </div>
        </li>
      `;
    },

    renderBundleItem(cart, bundleId) {
      // 收集该 bundle 的所有商品
      const bundleItems = cart.items.filter(item => 
        item.properties && item.properties._bundle_id === bundleId
      );
      
      if (bundleItems.length === 0) return '';
      
      // 获取主商品信息
      const firstItem = bundleItems[0];
      const mainTitle = firstItem.properties._bundle_main_title || 'Bundle Set';
      const mainImage = firstItem.properties._bundle_main_image || (firstItem.image ? firstItem.image.replace(/(\.[^.]+)$/, '_160x$1') : '');
      const mainUrl = firstItem.properties._bundle_main_url || firstItem.url;
      const mainComparePrice = parseInt(firstItem.properties._bundle_main_compare_price || '0', 10);
      const bundleQuantity = firstItem.quantity;
      
      // 计算总价和原价
      let bundleTotal = 0;
      let bundleOriginalTotal = 0;
      bundleItems.forEach(item => {
        bundleTotal += item.final_line_price;
        bundleOriginalTotal += item.original_line_price;
      });
      
      // 如果有主商品的 compare_at_price，用它作为划线价
      if (mainComparePrice > 0) {
        bundleOriginalTotal = mainComparePrice * bundleQuantity;
      }
      
      // 判断是否有折扣
      const hasDiscount = bundleOriginalTotal > bundleTotal;
      let discountPercent = 0;
      let savings = 0;
      if (hasDiscount) {
        savings = bundleOriginalTotal - bundleTotal;
        discountPercent = Math.round((savings / bundleOriginalTotal) * 100);
      }
      
      // 渲染子商品
      let childrenHtml = '';
      bundleItems.forEach(item => {
        const childImage = item.image ? item.image.replace(/(\.[^.]+)$/, '_80x$1') : '';
        const childVariant = item.variant_title && item.variant_title !== 'Default Title' 
          ? `<div class="cart-slide-drawer__bundle-child-variant">${this.escapeHtml(item.variant_title)}</div>` 
          : '<div class="cart-slide-drawer__bundle-child-variant"></div>';
        
        childrenHtml += `
          <div class="cart-slide-drawer__bundle-child" data-key="${item.key}">
            <div class="cart-slide-drawer__bundle-child-image">
              ${childImage ? `<img src="${childImage}" alt="${this.escapeHtml(item.product_title)}" loading="lazy">` : ''}
            </div>
            <div class="cart-slide-drawer__bundle-child-info">
              <div class="cart-slide-drawer__bundle-child-title">${this.escapeHtml(item.product_title)}</div>
              <div class="cart-slide-drawer__bundle-child-meta">
                ${childVariant}
                <div class="cart-slide-drawer__bundle-child-price">${this.formatMoney(item.price)}</div>
              </div>
            </div>
          </div>
        `;
      });
      
      // 价格显示HTML
      const priceHtml = hasDiscount 
        ? `<div class="cart-slide-drawer__bundle-price-wrap">
            <div class="cart-slide-drawer__bundle-price-row">
              <span class="cart-slide-drawer__bundle-price-original">${this.formatMoney(bundleOriginalTotal)}</span>
              <span class="cart-slide-drawer__bundle-total cart-slide-drawer__bundle-total--sale">${this.formatMoney(bundleTotal)}</span>
            </div>
            <div class="cart-slide-drawer__bundle-discount">(-${discountPercent}% Save ${this.formatMoney(savings)})</div>
          </div>`
        : `<div class="cart-slide-drawer__bundle-price-wrap">
            <div class="cart-slide-drawer__bundle-price-row">
              <span class="cart-slide-drawer__bundle-total">${this.formatMoney(bundleTotal)}</span>
            </div>
          </div>`;
      
      return `
        <li class="cart-slide-drawer__bundle" data-bundle-id="${bundleId}">
          <div class="cart-slide-drawer__bundle-header">
            <div class="cart-slide-drawer__bundle-image">
              ${mainImage ? `<img src="${mainImage}" alt="${this.escapeHtml(mainTitle)}" loading="lazy">` : ''}
            </div>
            <div class="cart-slide-drawer__bundle-info">
              <div class="cart-slide-drawer__bundle-info-top">
                <div class="cart-slide-drawer__bundle-info-left">
                  <div class="cart-slide-drawer__bundle-badge">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 6H16V4C16 2.89 15.11 2 14 2H10C8.89 2 8 2.89 8 4V6H4C2.89 6 2 6.89 2 8V19C2 20.11 2.89 21 4 21H20C21.11 21 22 20.11 22 19V8C22 6.89 21.11 6 20 6ZM10 4H14V6H10V4Z" fill="currentColor"/>
                    </svg>
                    Bundle · ${bundleItems.length} items
                  </div>
                  <a href="${mainUrl}" class="cart-slide-drawer__bundle-title">${this.escapeHtml(mainTitle)}</a>
                  <div class="cart-slide-drawer__bundle-options">
                    <div class="cart-slide-drawer__bundle-option">Quantity: ${bundleQuantity} Piece</div>
                    ${firstItem && firstItem.options_with_values ? firstItem.options_with_values.map(opt => `<div class="cart-slide-drawer__bundle-option">${opt.name}: ${opt.value}</div>`).join('') : ''}
                  </div>
                </div>
                <button type="button" class="cart-slide-drawer__bundle-remove" data-action="remove-bundle" data-bundle-id="${bundleId}" aria-label="Remove Bundle">
                  <img src="${this.drawer.dataset.removeIcon || ''}" alt="Remove" width="16" height="16">
                </button>
              </div>
              <div class="cart-slide-drawer__bundle-meta-row">
                <div class="cart-slide-drawer__bundle-qty-col">
                  <div class="cart-slide-drawer__bundle-qty">
                    <button type="button" class="cart-slide-drawer__quantity-btn${bundleQuantity <= 1 ? ' is-disabled' : ''}" data-action="decrease-bundle" data-bundle-id="${bundleId}" aria-label="Decrease quantity"${bundleQuantity <= 1 ? ' disabled' : ''}>
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0.599609 4.9751H9.34961" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </button>
                    <input type="number" class="cart-slide-drawer__bundle-quantity-input" data-bundle-id="${bundleId}" value="${bundleQuantity}" min="1" max="30" aria-label="Bundle Quantity">
                    <button type="button" class="cart-slide-drawer__quantity-btn${bundleQuantity >= 30 ? ' is-disabled' : ''}" data-action="increase-bundle" data-bundle-id="${bundleId}" aria-label="Increase quantity"${bundleQuantity >= 30 ? ' disabled' : ''}>
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M4.99581 0.600098L4.98438 9.3501" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M0.599609 4.9751H9.34961" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </button>
                  </div>
                </div>
                ${priceHtml}
              </div>
            </div>
          </div>
          <div class="cart-slide-drawer__bundle-items">
            ${childrenHtml}
          </div>
        </li>
      `;
    },

    async updateQuantity(line, change) {
      if (window.SicotasCartRateLimit?.isCoolingDown()) {
        return;
      }

      // 查找该行商品的数量显示元素（支持普通商品和主商品Bundle）
      let itemEl = this.drawer.querySelector(`.cart-slide-drawer__item[data-line="${line}"]`);
      if (!itemEl) {
        itemEl = this.drawer.querySelector(`.cart-slide-drawer__bundle[data-line="${line}"]`);
      }
      if (!itemEl) return;

      const input = itemEl.querySelector('.cart-slide-drawer__quantity-input');
      const currentQuantity = parseInt(input?.value, 10) || 0;
      const targetQuantity = Math.max(0, Math.min(30, currentQuantity + change));
      if (input) input.value = targetQuantity;

      // 快速连续点击只提交最后的目标数量，避免每次点击都产生 cart.js + change.js + section
      // 的请求突发。真正写入仍走按 item key 的互斥锁，不会与迁移写入并发覆盖。
      this.scheduleQuantityUpdate(line, targetQuantity, itemEl);
    },

    scheduleQuantityUpdate(line, quantity, itemEl) {
      const queueKey = itemEl?.dataset.key || `line:${line}`;
      const queued = this.quantityUpdateQueue.get(queueKey);
      if (queued) clearTimeout(queued.timer);

      const timer = setTimeout(() => {
        this.quantityUpdateQueue.delete(queueKey);
        this.setQuantity(line, quantity, itemEl).catch(error => {
          console.error('Cart Slide Drawer: Debounced quantity update failed', error);
        });
      }, 350);

      this.quantityUpdateQueue.set(queueKey, { timer, quantity });
    },

    // delta：+/- 按钮点击时传进来的增量（+1/-1）；不传时 quantity 就是要设置的绝对值
    // （直接输入框打字、删除商品都是这种用法，用户意图本来就是"设成这个数"，不需要重新对齐）
    async setQuantity(line, quantity, itemEl = null, delta = null) {
      try {
        // 单个商品 loading
        if (itemEl) {
          this.showItemLoading(itemEl);
        } else {
          // 如果没有传入 itemEl，尝试查找（支持普通商品和主商品Bundle）
          itemEl = this.drawer.querySelector(`.cart-slide-drawer__item[data-line="${line}"]`);
          if (!itemEl) {
            itemEl = this.drawer.querySelector(`.cart-slide-drawer__bundle[data-line="${line}"]`);
          }
          if (itemEl) {
            this.showItemLoading(itemEl);
          }
        }

        // 购物车行真正稳定的标识是 item.key，不是 line——line 只是位置索引，任何一行
        // 被背景迁移删除/合并都会让它后面所有行的 line 集体漂移。渲染时已经把 item.key
        // 写进了 data-key（见 renderCartItem），优先直接读，没有才退化成读一次最新购物车
        // 按位置换算
        let key = itemEl && itemEl.dataset.key;
        if (!key) {
          const snapshotCart = await this.getCartSnapshot();
          const currentItem = snapshotCart.items && snapshotCart.items[line - 1];
          if (!currentItem) {
            // 这一行已经不存在了（可能被背景迁移删除/合并），没什么好改的，刷新拿真实状态
            if (itemEl) this.hideItemLoading(itemEl);
            await this.refreshCart();
            return;
          }
          key = currentItem.key;
        }

        // 跟 cart-page-new.js 的 handleQuantityChange 共用同一把按 key 排队的互斥锁
        // （window.SicotasCartLineLock，定义在 fetch-interceptor.js）：购物车行的数量
        // 修改可能同时来自用户在这里的操作和背景迁移合并数量两条独立路径，Shopify 的
        // /cart/change.js 是写绝对值、没有原子自增，不排队的话后写入的一方会拿着自己
        // 那份稍旧的快照把对方刚写的值整个覆盖掉
        await window.SicotasCartLineLock.run(key, async () => {
          let targetQuantity = quantity;
          if (typeof delta === 'number') {
            // +/- 点击：拿到锁之后现读一次最新数量做基准再加/减，不信任点击那一刻的 DOM 值
            const freshCart = await this.getCartSnapshot({ force: true });
            const freshItem = (freshCart.items || []).find(i => i.key === key);
            const fallbackQty = parseInt(itemEl && itemEl.querySelector('.cart-slide-drawer__quantity-input')?.value, 10) || 0;
            targetQuantity = Math.max(0, Math.min(30, (freshItem ? freshItem.quantity : fallbackQty) + delta));
          }

          const response = await fetch('/cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              id: key,
              quantity: targetQuantity,
              sections: ['cart-slide-drawer-content'],
              sections_url: window.location.pathname
            })
          });

          if (response.status === 429) {
            const input = itemEl?.querySelector('.cart-slide-drawer__quantity-input');
            if (input) input.value = input.defaultValue;
            if (itemEl) this.hideItemLoading(itemEl);
            return;
          }
          if (!response.ok) {
            throw new Error(`Cart quantity update failed: ${response.status}`);
          }

          const cart = await response.json();

          // 检查 API 是否返回错误（库存不足等）
          if (cart.status === 422 || cart.description) {
            // 显示库存不足警告
            this.showStockWarning(itemEl, cart.description || 'Insufficient stock');
            if (itemEl) {
              this.hideItemLoading(itemEl);
            }
            // 刷新购物车以获取正确状态
            await this.refreshCart();
            return;
          }

          await this.renderCart(cart, cart.sections?.['cart-slide-drawer-content']);

          // Update header cart count if exists (使用计算后的显示数量)
          this.updateHeaderCartCount(this.calculateDisplayCount(cart));

          // Dispatch cart change event
          document.dispatchEvent(new CustomEvent('cart:change', { detail: { cart } }));
        });

      } catch (error) {
        console.error('Cart Slide Drawer: Error updating quantity', error);
        if (itemEl) {
          this.hideItemLoading(itemEl);
        }
        if (!window.SicotasCartRateLimit?.isCoolingDown()) this.refreshCart();
      }
    },
    
    // 显示库存警告toast
    showStockWarning(itemEl, message) {
      this.showToast(message);
    },
    
    // 显示Bundle库存警告toast
    showBundleStockWarning(bundleEl, message) {
      this.showToast(message);
    },
    
    // 折扣码应用功能
    async applyDiscountCode() {
      const input = this.drawer.querySelector('.cart-slide-drawer__discount-input');
      const btn = this.drawer.querySelector('.cart-slide-drawer__discount-btn');
      if (!input) return;
      
      const code = input.value.trim();
      
      // 检查是否输入了折扣码
      if (!code) {
        this.showDiscountError('Please enter a discount code');
        return;
      }
      
      // 显示loading状态
      if (btn) {
        btn.disabled = true;
        btn.textContent = 'Applying...';
      }
      
      try {
        // 访问 /discount/CODE 页面，Shopify 会将折扣码存入 cookie
        await fetch('/discount/' + encodeURIComponent(code), {
          redirect: 'follow'
        });
        
        // 刷新购物车以获取最新状态
        if (window.SicotasCartSnapshot) window.SicotasCartSnapshot.invalidate();
        const cart = await this.getCartSnapshot({ force: true });
        
        // 检查购物车是否有折扣应用
        const hasCartDiscount = cart.cart_level_discount_applications && cart.cart_level_discount_applications.length > 0;
        const hasLineDiscount = cart.items && cart.items.some(item => 
          item.line_level_discount_allocations && item.line_level_discount_allocations.length > 0
        );
        
        if (hasCartDiscount || hasLineDiscount) {
          this.showDiscountSuccess('Discount code applied!');
          input.value = '';
          await this.renderCart(cart);
        } else {
          // 折扣码无效或不适用
          this.showDiscountError('Invalid or inapplicable discount code');
        }
      } catch (error) {
        console.error('Cart Slide Drawer: Error applying discount code', error);
        this.showDiscountError('Failed to apply discount code');
      } finally {
        // 恢复按钮状态
        if (btn) {
          btn.disabled = false;
          btn.textContent = 'Apply';
        }
      }
    },
    
    // 显示折扣码错误提示
    showDiscountError(message) {
      let errorEl = this.drawer.querySelector('.cart-slide-drawer__discount-error');
      const form = this.drawer.querySelector('.cart-slide-drawer__discount-form');
      
      if (!errorEl && form) {
        errorEl = document.createElement('div');
        errorEl.className = 'cart-slide-drawer__discount-error';
        form.insertAdjacentElement('afterend', errorEl);
      }
      
      if (errorEl) {
        errorEl.textContent = message;
        errorEl.classList.add('is-visible');
      }
      
      // 给输入框添加错误样式
      const input = this.drawer.querySelector('.cart-slide-drawer__discount-input');
      if (input) {
        input.classList.add('is-error');
      }
    },
    
    // 隐藏折扣码错误提示
    hideDiscountError() {
      const errorEl = this.drawer.querySelector('.cart-slide-drawer__discount-error');
      if (errorEl) {
        errorEl.classList.remove('is-visible');
      }
      
      const input = this.drawer.querySelector('.cart-slide-drawer__discount-input');
      if (input) {
        input.classList.remove('is-error');
      }
    },
    
    // 显示折扣码成功提示
    showDiscountSuccess(message) {
      let successEl = this.drawer.querySelector('.cart-slide-drawer__discount-success');
      const form = this.drawer.querySelector('.cart-slide-drawer__discount-form');
      
      if (!successEl && form) {
        successEl = document.createElement('div');
        successEl.className = 'cart-slide-drawer__discount-success';
        form.insertAdjacentElement('afterend', successEl);
      }
      
      if (successEl) {
        successEl.textContent = message;
        successEl.classList.add('is-visible');
        
        // 3秒后隐藏
        setTimeout(() => {
          successEl.classList.remove('is-visible');
        }, 3000);
      }
    },
    
    // 显示全局toast提示
    showToast(message) {
      let toast = document.querySelector('.cart-slide-drawer__toast');
      if (!toast) {
        toast = document.createElement('div');
        toast.className = 'cart-slide-drawer__toast';
        document.body.appendChild(toast);
      }
      
      toast.textContent = message;
      toast.classList.add('is-visible');
      
      // 3秒后隐藏
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => {
        toast.classList.remove('is-visible');
      }, 3000);
    },

    async removeItem(line) {
      // 支持普通商品和主商品Bundle
      let itemEl = this.drawer.querySelector(`.cart-slide-drawer__item[data-line="${line}"]`);
      if (!itemEl) {
        itemEl = this.drawer.querySelector(`.cart-slide-drawer__bundle[data-line="${line}"]`);
      }
      const queueKey = itemEl?.dataset.key || `line:${line}`;
      const queued = this.quantityUpdateQueue.get(queueKey);
      if (queued) {
        clearTimeout(queued.timer);
        this.quantityUpdateQueue.delete(queueKey);
      }
      await this.setQuantity(line, 0, itemEl);
    },

    // 显示单个商品 loading
    showItemLoading(itemEl) {
      if (!itemEl) return;
      itemEl.classList.add('is-loading');
    },

    // 隐藏单个商品 loading
    hideItemLoading(itemEl) {
      if (!itemEl) return;
      itemEl.classList.remove('is-loading');
    },

    // Bundle 的连续 +/- 同样合并为一次绝对数量写入，避免每次点击都读取并更新整组商品。
    async updateBundleQuantity(bundleId, change) {
      if (window.SicotasCartRateLimit?.isCoolingDown()) {
        return;
      }

      const bundleEl = this.drawer.querySelector(`.cart-slide-drawer__bundle[data-bundle-id="${bundleId}"]`);
      const input = bundleEl?.querySelector('.cart-slide-drawer__bundle-quantity-input');
      const currentQuantity = parseInt(input?.value, 10) || 0;
      const targetQuantity = Math.max(0, Math.min(30, currentQuantity + change));
      if (input) input.value = targetQuantity;

      const queued = this.bundleQuantityUpdateQueue.get(bundleId);
      if (queued) clearTimeout(queued.timer);
      const timer = setTimeout(() => {
        this.bundleQuantityUpdateQueue.delete(bundleId);
        this.setBundleQuantity(bundleId, targetQuantity);
      }, 350);
      this.bundleQuantityUpdateQueue.set(bundleId, { timer, quantity: targetQuantity });
    },

    async removeBundleItems(bundleId) {
      const queued = this.bundleQuantityUpdateQueue.get(bundleId);
      if (queued) {
        clearTimeout(queued.timer);
        this.bundleQuantityUpdateQueue.delete(bundleId);
      }
      await this.setBundleQuantity(bundleId, 0);
    },

    // newQty 是用户直接输入的绝对值（跟 updateBundleQuantity 的增量语义不同），
    // 意图本来就是"设成这个数"，不需要跟最新数量重新对齐，但写入本身仍然要走
    // runMulti 排队，避免跟背景迁移合并数量的写入产生中间态
    async setBundleQuantity(bundleId, newQty) {
      try {
        // 单个 Bundle loading
        const bundleEl = this.drawer.querySelector(`.cart-slide-drawer__bundle[data-bundle-id="${bundleId}"]`);
        if (bundleEl) {
          bundleEl.classList.add('is-loading');
        }

        // 获取当前购物车，找到所有属于该 bundle 的商品 key
        const cart = await this.getCartSnapshot();
        const bundleItems = cart.items.filter(item => {
          return item.properties && item.properties._bundle_id === bundleId;
        });
        if (bundleItems.length === 0) return;
        const bundleKeys = bundleItems.map(item => item.key);

        const quantity = Math.max(0, Math.min(30, newQty));

        const updatedCart = await window.SicotasCartLineLock.runMulti(bundleKeys, async () => {
          const updates = {};
          bundleKeys.forEach(key => {
            updates[key] = quantity;
          });

          const response = await fetch('/cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              updates,
              sections: ['cart-slide-drawer-content'],
              sections_url: window.location.pathname
            })
          });

          if (response.status === 429) {
            const input = bundleEl?.querySelector('.cart-slide-drawer__bundle-quantity-input');
            if (input) input.value = input.defaultValue;
            if (bundleEl) bundleEl.classList.remove('is-loading');
            return null;
          }
          if (!response.ok) {
            throw new Error(`Cart bundle update failed: ${response.status}`);
          }

          return response.json();
        });

        if (!updatedCart) return;
        await this.renderCart(updatedCart, updatedCart.sections?.['cart-slide-drawer-content']);

        // Update header cart count (使用计算后的显示数量)
        this.updateHeaderCartCount(this.calculateDisplayCount(updatedCart));

        // Dispatch cart change event
        document.dispatchEvent(new CustomEvent('cart:change', { detail: { cart: updatedCart } }));
        
      } catch (error) {
        console.error('Cart Slide Drawer: Error setting bundle quantity', error);
        if (!window.SicotasCartRateLimit?.isCoolingDown()) this.refreshCart();
      }
    },

    updateHeaderCartCount(count) {
      // Update various cart count elements in the header
      const countElements = document.querySelectorAll(
        '.cart-link__bubble-num, .cart-count, [data-cart-count], .js-cart-count'
      );
      countElements.forEach(el => {
        el.textContent = count;
      });

      // Handle cart bubble visibility
      const bubbles = document.querySelectorAll('.cart-link__bubble');
      bubbles.forEach(bubble => {
        // 部分外部 App 会直接给 bubble 设置 textContent，导致内部数量节点被删除。
        // 每次更新时补回标准结构，确保后续加购仍有稳定的更新目标。
        let bubbleNum = bubble.querySelector('.cart-link__bubble-num');
        if (!bubbleNum) {
          bubble.textContent = '';
          bubbleNum = document.createElement('span');
          bubbleNum.className = 'cart-link__bubble-num';
          bubble.appendChild(bubbleNum);
        }
        bubbleNum.textContent = count;

        if (count > 0) {
          bubble.classList.add('cart-link__bubble--visible');
        } else {
          bubble.classList.remove('cart-link__bubble--visible');
        }
      });
    },

    // 计算购物车显示数量：普通商品数量 + bundle套数（不是子商品数量）
    calculateDisplayCount(cart) {
      let displayCount = 0;
      const countedBundleIds = new Set();
      
      cart.items.forEach(item => {
        const bundleId = item.properties && item.properties._bundle_id;
        
        if (bundleId) {
          // 是 bundle 子商品，检查是否已计算过该 bundle
          if (!countedBundleIds.has(bundleId)) {
            // 第一次遇到该 bundle，计入 bundle 数量（按套数）
            displayCount += item.quantity;
            countedBundleIds.add(bundleId);
          }
        } else {
          // 普通商品，直接计入数量
          displayCount += item.quantity;
        }
      });
      
      return displayCount;
    },

    formatMoney(cents) {
      // Simple money formatting - adjust based on store's currency format
      const amount = (cents / 100).toFixed(2);
      return '$' + amount;
    },

    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    },

    // Initialize custom scrollbar for recommendations
    initRecommendationsScrollbar() {
      const container = document.getElementById('cart-slide-drawer-rec-grid');
      const thumb = document.querySelector('.cart-slide-drawer__scrollbar-thumb');
      const track = document.querySelector('.cart-slide-drawer__scrollbar-track');

      if (!container || !thumb || !track) return;

      const self = this;
      let isDragging = false;
      let startX, startLeft;

      // Update thumb position and width based on scroll
      function updateThumb() {
        const scrollWidth = container.scrollWidth;
        const clientWidth = container.clientWidth;
        const scrollLeft = container.scrollLeft;
        const trackWidth = track.offsetWidth;

        // Hide scrollbar if content fits
        if (scrollWidth <= clientWidth) {
          thumb.style.display = 'none';
          return;
        } else {
          thumb.style.display = 'block';
        }

        // Calculate thumb width proportionally
        const thumbWidth = Math.max((clientWidth / scrollWidth) * trackWidth, 40);
        thumb.style.width = thumbWidth + 'px';

        // Calculate thumb position
        const maxScrollLeft = scrollWidth - clientWidth;
        const maxThumbLeft = trackWidth - thumbWidth;
        const thumbLeft = maxScrollLeft > 0 ? (scrollLeft / maxScrollLeft) * maxThumbLeft : 0;
        thumb.style.left = thumbLeft + 'px';
      }

      // Start dragging
      function startDrag(clientX) {
        isDragging = true;
        startX = clientX;
        startLeft = thumb.offsetLeft;
        document.body.style.userSelect = 'none';
        thumb.style.cursor = 'grabbing';
      }

      // During drag
      function doDrag(clientX) {
        if (!isDragging) return;

        const trackWidth = track.offsetWidth;
        const thumbWidth = thumb.offsetWidth;

        let newLeft = startLeft + (clientX - startX);
        newLeft = Math.max(0, Math.min(newLeft, trackWidth - thumbWidth));

        thumb.style.left = newLeft + 'px';

        // Sync scroll position
        const scrollRatio = newLeft / (trackWidth - thumbWidth);
        const maxScrollLeft = container.scrollWidth - container.clientWidth;
        container.scrollLeft = scrollRatio * maxScrollLeft;
      }

      // End dragging
      function endDrag() {
        if (!isDragging) return;
        isDragging = false;
        document.body.style.userSelect = '';
        thumb.style.cursor = 'grab';
      }

      // Mouse events
      thumb.addEventListener('mousedown', (e) => {
        e.preventDefault();
        startDrag(e.clientX);
      });

      document.addEventListener('mousemove', (e) => {
        if (isDragging) doDrag(e.clientX);
      });

      document.addEventListener('mouseup', endDrag);

      // Touch events for mobile
      thumb.addEventListener('touchstart', (e) => {
        startDrag(e.touches[0].clientX);
      }, { passive: true });

      document.addEventListener('touchmove', (e) => {
        if (isDragging) {
          doDrag(e.touches[0].clientX);
        }
      }, { passive: true });

      document.addEventListener('touchend', endDrag);

      // Sync thumb on scroll
      container.addEventListener('scroll', updateThumb);

      // Update on window resize
      window.addEventListener('resize', updateThumb);

      // Initial update
      updateThumb();

      // Store reference for later re-init if needed
      this.recScrollbar = { container, thumb, track, updateThumb };
    },

    // Initialize recommendation swatches - click to switch image
    initRecommendationSwatches() {
      const recContainer = document.getElementById('cart-slide-drawer-recommendations');
      if (!recContainer) return;

      // Handle swatch clicks
      recContainer.addEventListener('click', (e) => {
        const swatch = e.target.closest('.cart-slide-drawer__rec-swatch');
        if (swatch) {
          e.preventDefault();
          e.stopPropagation();
          
          const recItem = swatch.closest('.cart-slide-drawer__rec-item');
          const mainImg = recItem.querySelector('.cart-slide-drawer__rec-main-img');
          const variantImage = swatch.dataset.variantImage;
          
          // Update active state
          recItem.querySelectorAll('.cart-slide-drawer__rec-swatch').forEach(s => s.classList.remove('is-active'));
          swatch.classList.add('is-active');
          
          // Switch image if variant has image
          if (variantImage && mainImg) {
            mainImg.src = variantImage;
          }
          return;
        }

        // Handle product card click (navigate to product page)
        const recItem = e.target.closest('.cart-slide-drawer__rec-item');
        if (recItem && !e.target.closest('.cart-slide-drawer__rec-swatch')) {
          const productUrl = recItem.dataset.productUrl;
          if (productUrl) {
            window.location.href = productUrl;
          }
        }
      });
    },

    // 在对应商品行上显示持久提示（不自动消失），用于拆分商品后找不到迁移映射的场景
    showMigrationAnomalyBadge(itemEl, message) {
      if (!itemEl) return;

      const optionsEl = itemEl.querySelector('.cart-slide-drawer__item-options');
      if (!optionsEl) return;

      let badgeEl = optionsEl.querySelector('.cart-slide-drawer__migration-badge');
      if (!badgeEl) {
        badgeEl = document.createElement('div');
        badgeEl.className = 'cart-slide-drawer__migration-badge';
        optionsEl.appendChild(badgeEl);
      }
      badgeEl.textContent = message;
    },

    // 全屏蒙层：迁移期间盖住整个视口（包括抽屉下方仍然可见的 Checkout 按钮），
    // 跟购物车页面的 .cart-migration-overlay 视觉上保持一致
    showMigrationOverlay() {
      if (document.querySelector('.cart-migration-overlay')) return;
      const overlay = document.createElement('div');
      overlay.className = 'cart-migration-overlay';
      overlay.innerHTML = '<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#6D4C41" stroke-width="2.5"><circle cx="12" cy="12" r="10" stroke-opacity="0.2"/><path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round"><animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.6s" repeatCount="indefinite"/></path></svg>';
      document.body.appendChild(overlay);
    },

    hideMigrationOverlay() {
      const overlay = document.querySelector('.cart-migration-overlay');
      if (overlay) overlay.remove();
    },

    // 等新渲染出的商品行图片全部加载完（或超时兜底）再放行，避免图片异步加载撑开/
    // 收缩行高导致的版面变化发生在蒙层已经消失之后，把下方 Checkout 按钮顶到用户刚点的位置
    waitForImagesToLoad(container, timeoutMs) {
      if (!container) return Promise.resolve();
      const imgs = Array.from(container.querySelectorAll('img'));
      if (!imgs.length) return Promise.resolve();
      return Promise.all(imgs.map(img => {
        if (img.complete) return Promise.resolve();
        return new Promise(resolve => {
          const done = () => resolve();
          img.addEventListener('load', done, { once: true });
          img.addEventListener('error', done, { once: true });
          setTimeout(done, timeoutMs);
        });
      }));
    },

    // 等浏览器真正完成一次布局与绘制，确保刚写入的 DOM 变更已经反映到屏幕上，
    // 而不是还停留在"已提交但未绘制"的状态
    waitForLayoutSettled() {
      return new Promise(resolve => {
        requestAnimationFrame(() => requestAnimationFrame(resolve));
      });
    },

    // 检测并静默迁移购物车里因商品拆分而失效的 variant；找不到映射的保留原样并标记异常。
    // 页面加载（init）和每次打开抽屉（open）都会触发，两次调用共用同一个 in-flight promise，
    // 避免前后脚重叠触发时各自拿着过期的购物车快照互相抢跑。
    migrateStaleCartVariants() {
      if (this._migrateCartPromise) return this._migrateCartPromise;
      this._migrateCartPromise = this._doMigrateStaleCartVariants().finally(() => {
        this._migrateCartPromise = null;
      });
      return this._migrateCartPromise;
    },

    async _doMigrateStaleCartVariants() {
      if (!window.VariantMigration) return;
      // 购物车页面自己（cart-page-new.js）已经在做同样的检测和迁移，抽屉这里是全站加载的，
      // 两边同时跑会各自独立操作购物车、互相抢跑，导致后完成的一方删除已经被对方删掉的旧行而 400。
      // 购物车页面自己会处理，这里直接跳过。
      if (document.querySelector('#new-cart-items')) return;

      let cart;
      try {
        // 优先走 fetch-interceptor.js 里的共享短 TTL 快照缓存：加购后打开抽屉这个时间点，
        // fetch-interceptor.js 自己的写后 diff 检测大概率刚读过一次一模一样的"当前购物车"，
        // 复用它的结果能省掉一次重复的 /cart.js 请求（见该文件 SicotasCartSnapshot 定义处
        // 的注释）。cache: 'no-store' 防止拿到预取/预渲染缓存的过期快照——迁移检测/删除
        // 决策都是从这份快照出发的，读到旧数据会导致误判（见 variant-migration.js 同类修复）
        cart = window.SicotasCartSnapshot
          ? await window.SicotasCartSnapshot.get()
          : await fetch('/cart.js', { cache: 'no-store' }).then(r => r.json());
      } catch (error) {
        return;
      }

      // 蒙层必须盖住从"检测到失效商品"到"新商品+优惠券完全替换完毕、抽屉重新渲染完成"
      // 的整个过程，中途不能有任何空档，否则用户可能点到中间态下方仍可见的 Checkout /
      // View Cart 按钮。安全兜底定时器只用来防止极端情况下（网络卡死等）蒙层永久卡死，
      // 正常流程里 finally 会先一步清掉它、不会提前误触发
      let safetyTimer = null;
      try {
        // 注意：这里不整体套 SicotasCartLineLock，原因见 cart-page-new.js 的
        // migrateStaleCartVariants 同类改动的注释——扫描阶段只读、可能耗时不短，
        // 整体套锁会导致每次打开抽屉都先卡住 loading 一段时间才能操作数量
        const result = await window.VariantMigration.migrateCart(cart, {
          // 检测阶段一确认真的有失效商品就立刻盖蒙层，尽量在实际迁移开始前
          // 给用户留出操作窗口，避免正常购物车也跟着闪一下
          onStaleDetected: () => {
            this.showMigrationOverlay();
            safetyTimer = setTimeout(() => this.hideMigrationOverlay(), 30000);
          }
        });

        if (result.migratedCount > 0) {
          await this.refreshCart(result.cart);
          // 新行的图片加载完、浏览器完成一次真实绘制之后，画面才算真正稳定，
          // 此时才是蒙层可以放行的最早时机
          await this.waitForImagesToLoad(this.itemsWrap, 3000);
          await this.waitForLayoutSettled();
        }

        result.anomalies.forEach(item => {
          const itemEl = this.drawer && this.drawer.querySelector(`.cart-slide-drawer__item[data-key="${item.key}"]`);
          this.showMigrationAnomalyBadge(itemEl, 'This item has been updated. Please remove and reselect it.');
        });
      } finally {
        if (safetyTimer) clearTimeout(safetyTimer);
        this.hideMigrationOverlay();
      }
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => CartSlideDrawer.init());
  } else {
    CartSlideDrawer.init();
  }

  // Export for external access
  window.CartSlideDrawer = CartSlideDrawer;
})();
